import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http, formatErr } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Plus, Pencil, Trash, FloppyDisk, X, Tag } from "@phosphor-icons/react";

export default function Brands() {
  const qc = useQueryClient();
  const { user } = useAuth();
  const { data: brands = [] } = useQuery({ queryKey: ["brands"], queryFn: () => http.get("/brands").then(r => r.data) });
  const { data: companies = [] } = useQuery({ queryKey: ["companies"], queryFn: () => http.get("/companies").then(r => r.data) });
  const { data: stores = [] } = useQuery({ queryKey: ["stores"], queryFn: () => http.get("/stores").then(r => r.data) });
  const cMap = Object.fromEntries(companies.map(c => [c.id, c.name]));
  const storeCount = (bid) => stores.filter(s => s.brand_id === bid).length;

  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ name: "", company_id: "" });
  const [err, setErr] = useState(""); const [busy, setBusy] = useState(false);

  const canCreate = ["admin", "general_manager"].includes(user?.role);
  const canEdit = canCreate;
  const canDelete = user?.role === "admin";

  const open = (mode, b = { name: "", company_id: "" }) => { setForm({ name: b.name || "", company_id: b.company_id || "" }); setModal({ mode, id: b.id }); setErr(""); };
  const close = () => { setModal(null); setErr(""); };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      if (modal.mode === "create") await http.post("/brands", form);
      else await http.patch(`/brands/${modal.id}`, form);
      qc.invalidateQueries({ queryKey: ["brands"] });
      close();
    } catch (ex) { setErr(formatErr(ex?.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Soft-delete brand "${name}"?`)) return;
    try { await http.delete(`/brands/${id}`); qc.invalidateQueries({ queryKey: ["brands"] }); }
    catch (ex) { alert(formatErr(ex?.response?.data?.detail)); }
  };

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-end justify-between mb-6">
        <div>
          <div className="overline">Tenancy</div>
          <h1 className="text-3xl font-heading font-semibold tracking-tight mt-1">Brands</h1>
        </div>
        {canCreate && <button onClick={() => open("create")} data-testid="brand-new-button"
          className="inline-flex items-center gap-2 h-10 px-4 rounded-md text-white text-sm font-medium" style={{ background: "#002FA7" }}>
          <Plus size={14} weight="bold" /> Add brand
        </button>}
      </div>

      <div className="rmms-card overflow-hidden">
        <table className="w-full text-sm">
          <thead><tr className="text-left text-[11px] font-heading uppercase tracking-wider text-slate-500 border-b border-slate-200">
            <th className="px-4 py-2.5">Brand</th><th className="px-4 py-2.5">Company</th><th className="px-4 py-2.5">Stores</th><th className="px-4 py-2.5 w-24"></th>
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {brands.map(b => (
              <tr key={b.id} data-testid={`brand-row-${b.id}`}>
                <td className="px-4 py-3 font-medium flex items-center gap-2"><Tag size={12} /> {b.name}</td>
                <td className="px-4 py-3 text-slate-600">{cMap[b.company_id]}</td>
                <td className="px-4 py-3 font-mono-data">{storeCount(b.id)}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-1 justify-end">
                    {canEdit && <button onClick={() => open("edit", b)} data-testid={`brand-edit-${b.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-slate-50"><Pencil size={12} /></button>}
                    {canDelete && <button onClick={() => remove(b.id, b.name)} data-testid={`brand-delete-${b.id}`} className="h-7 w-7 border border-slate-200 rounded-sm flex items-center justify-center hover:bg-rose-50 hover:text-rose-600"><Trash size={12} /></button>}
                  </div>
                </td>
              </tr>
            ))}
            {brands.length === 0 && <tr><td colSpan={4} className="px-4 py-10 text-center text-sm text-slate-500">No brands yet.</td></tr>}
          </tbody>
        </table>
      </div>

      {modal && (
        <div className="fixed inset-0 bg-slate-900/50 flex items-center justify-center p-4 z-50" data-testid="brand-modal">
          <form onSubmit={submit} className="bg-white border border-slate-200 rounded-md w-full max-w-md p-6 space-y-4">
            <div className="flex items-center justify-between">
              <div><div className="overline">{modal.mode === "create" ? "New" : "Edit"}</div><h2 className="text-xl font-heading font-semibold tracking-tight mt-1">Brand</h2></div>
              <button type="button" onClick={close} className="h-8 w-8 rounded-sm hover:bg-slate-100 flex items-center justify-center"><X size={16} /></button>
            </div>
            <div>
              <label className="overline mb-1.5 block">Name *</label>
              <input required value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} data-testid="brand-name-input" className="rmms-input" />
            </div>
            <div>
              <label className="overline mb-1.5 block">Company *</label>
              <select required value={form.company_id} onChange={e => setForm({ ...form, company_id: e.target.value })} data-testid="brand-company-select" className="rmms-input">
                <option value="">Select…</option>
                {companies.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>
            {err && <div className="text-sm text-rose-600 border-l-2 border-rose-500 pl-3 py-1">{err}</div>}
            <div className="flex justify-end gap-2 pt-2 border-t border-slate-200">
              <button type="button" onClick={close} className="h-10 px-4 text-sm border border-slate-200 rounded-md">Cancel</button>
              <button type="submit" disabled={busy} data-testid="brand-submit-button" className="h-10 px-4 rounded-md text-white text-sm inline-flex items-center gap-1.5" style={{ background: "#002FA7" }}>
                <FloppyDisk size={14} weight="bold" /> {busy ? "Saving…" : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
